import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  Fabric, 
  fabricClassValueToLabel, 
  fabricTypeValueToLabel, 
  dyeingTypeValueToLabel,
  currencyValueToLabel,
  priceUnitValueToLabel
} from '../models/Fabric';
import { getFabricById, deleteFabric } from '../services/fabricService';
import { toast } from 'react-toastify';
import { API_URL } from '../config/constants';
import ImageModal from '../components/ImageModal';

const FabricDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [fabric, setFabric] = useState<Fabric | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal State
  const [modalOpen, setModalOpen] = useState(false);
  const [modalImage, setModalImage] = useState('');
  const [modalAltText, setModalAltText] = useState('');

  const openModal = (imageUrl: string, altText: string) => {
    setModalImage(imageUrl);
    setModalAltText(altText);
    setModalOpen(true);
  };

  useEffect(() => {
    const loadFabric = async () => {
      try {
        setLoading(true);
        if (id) {
          const data = await getFabricById(id);
          setFabric(data);
          console.log('Yüklenen kumaş detayları:', data);
        }
        setError(null);
      } catch (err) {
        console.error('Kumaş detayları yüklenirken hata oluştu:', err);
        setError('Kumaş detayları yüklenirken bir hata oluştu. Lütfen daha sonra tekrar deneyin.');
        toast.error('Kumaş detayları yüklenirken bir hata oluştu');
      } finally {
        setLoading(false);
      }
    };

    loadFabric();
  }, [id]);

  const handleDelete = async () => {
    if (!fabric || !fabric._id) return;

    if (window.confirm('Bu kumaşı silmek istediğinize emin misiniz? Bu işlem geri alınamaz.')) {
      try {
        await deleteFabric(fabric._id);
        toast.success('Kumaş başarıyla silindi');
        navigate('/fabrics');
      } catch (err) {
        console.error('Kumaş silinirken hata oluştu:', err);
        toast.error('Kumaş silinirken bir hata oluştu');
      }
    }
  };

  // Türkçe etiketleri görüntülemek için helper function
  const getLocalizedValue = (key: string, value: string): string => {
    switch (key) {
      case 'fabricClass':
        return fabricClassValueToLabel(value);
      case 'type':
        return fabricTypeValueToLabel(value);
      case 'dyeingType':
        return dyeingTypeValueToLabel(value);
      default:
        return value;
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  if (error || !fabric) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4">
          <p>{error || 'Kumaş bulunamadı'}</p>
          <Link 
            to="/fabrics"
            className="mt-2 text-red-600 hover:text-red-800 font-medium flex items-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M9.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L7.414 9H15a1 1 0 110 2H7.414l2.293 2.293a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
            Kumaşlar listesine dön
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* ImageModal bileşenini kullan */}
      <ImageModal 
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        imageUrl={modalImage}
        altText={modalAltText}
      />

      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <Link
            to="/fabrics"
            className="flex items-center mr-4 text-blue-600 hover:text-blue-800 transition duration-200"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M9.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L7.414 9H15a1 1 0 110 2H7.414l2.293 2.293a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
            <span>Geri</span>
          </Link>
          <h1 className="text-2xl font-bold text-gray-800">{fabric.name}</h1>
        </div>
        <div className="flex space-x-3">
          <Link
            to={`/fabrics/${fabric._id}/edit`}
            className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-md shadow-sm transition duration-200 flex items-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Düzenle
          </Link>
          <button
            onClick={handleDelete}
            className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-md shadow-sm transition duration-200 flex items-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Sil
          </button>
        </div>
      </div>

      <div className="bg-white shadow-lg rounded-lg overflow-hidden border border-gray-200">
        <div className="md:flex">
          {/* Sol taraf - Görseller */}
          <div className="md:w-1/3 p-6 border-r border-gray-200 bg-gray-50">
            {/* Kumaş Görseli */}
            <div className="mb-6">
              <h2 className="text-lg font-semibold mb-3 text-blue-800 border-b border-blue-200 pb-2 flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                </svg>
                Kumaş Görseli
              </h2>
              {fabric.imageUrl ? (
                <div 
                  className="rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity transform hover:scale-[1.02] duration-200 shadow-sm border border-gray-200"
                  onClick={() => openModal(
                    `${API_URL}${fabric.imageUrl.startsWith('/') ? '' : '/'}${fabric.imageUrl}`, 
                    `${fabric.name} görseli`
                  )}
                >
                  <img
                    src={`${API_URL}${fabric.imageUrl.startsWith('/') ? '' : '/'}${fabric.imageUrl}`}
                    alt={fabric.name}
                    className="w-full h-64 object-cover"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = '/placeholder-fabric.png';
                    }}
                  />
                  <div className="mt-2 text-center text-sm text-blue-600 py-1.5 bg-blue-50 border-t border-blue-100">
                    <span className="flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                      </svg>
                      Büyütmek için tıklayın
                    </span>
                  </div>
                </div>
              ) : (
                <div className="w-full h-48 bg-gray-100 flex flex-col items-center justify-center rounded-lg text-gray-500 border border-gray-200">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span className="mt-2">Görsel Yok</span>
                </div>
              )}
            </div>
            
            {/* Kumaş Örneği */}
            {fabric.swatch && (
              <div className="mb-6">
                <h2 className="text-lg font-semibold mb-3 text-blue-800 border-b border-blue-200 pb-2 flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM14 11a1 1 0 011 1v1h1a1 1 0 110 2h-1v1a1 1 0 11-2 0v-1h-1a1 1 0 110-2h1v-1a1 1 0 011-1z" />
                  </svg>
                  Kumaş Örneği
                </h2>
                <div 
                  className="rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity transform hover:scale-[1.02] duration-200 shadow-sm border border-gray-200"
                  onClick={() => openModal(
                    `${API_URL}${fabric.swatch.startsWith('/') ? '' : '/'}${fabric.swatch}`, 
                    `${fabric.name} kumaş örneği`
                  )}
                >
                  <img
                    src={`${API_URL}${fabric.swatch.startsWith('/') ? '' : '/'}${fabric.swatch}`}
                    alt="Kumaş örneği"
                    className="w-full h-48 object-cover"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = '/placeholder-swatch.png';
                    }}
                  />
                  <div className="mt-2 text-center text-sm text-blue-600 py-1.5 bg-blue-50 border-t border-blue-100">
                    <span className="flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                      </svg>
                      Büyütmek için tıklayın
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sağ taraf - Detaylı Teknik Bilgiler */}
          <div className="md:w-2/3 p-6">
            {/* Temel Bilgiler */}
            <div className="mb-8">
              <h2 className="text-xl font-bold mb-4 text-gray-800 border-b border-gray-200 pb-2 flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                Temel Bilgiler
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-blue-50/60 p-4 rounded-lg border border-blue-100">
                  <table className="w-full text-sm">
                    <tbody className="divide-y divide-blue-100">
                      <tr>
                        <td className="py-2.5 pr-4 text-gray-700 font-medium">Kumaş Adı:</td>
                        <td className="py-2.5 font-semibold text-blue-800">{fabric.name}</td>
                      </tr>
                      <tr>
                        <td className="py-2.5 pr-4 text-gray-700 font-medium">Kumaş Sınıfı:</td>
                        <td className="py-2.5">{getLocalizedValue('fabricClass', fabric.fabricClass)}</td>
                      </tr>
                      <tr>
                        <td className="py-2.5 pr-4 text-gray-700 font-medium">Kumaş Tipi:</td>
                        <td className="py-2.5">{getLocalizedValue('type', fabric.type)}</td>
                      </tr>
                      <tr>
                        <td className="py-2.5 pr-4 text-gray-700 font-medium">İplik Numarası:</td>
                        <td className="py-2.5">{fabric.yarnNumber || '-'}</td>
                      </tr>
                      <tr>
                        <td className="py-2.5 pr-4 text-gray-700 font-medium">İçerik:</td>
                        <td className="py-2.5">{fabric.composition}</td>
                      </tr>
                      <tr>
                        <td className="py-2.5 pr-4 text-gray-700 font-medium">Boyama Çeşidi:</td>
                        <td className="py-2.5">{getLocalizedValue('dyeingType', fabric.dyeingType)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Teknik Bilgiler */}
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <h3 className="text-lg font-semibold mb-3 text-gray-700 flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                    </svg>
                    Teknik Özellikler
                  </h3>
                  <table className="w-full text-sm">
                    <tbody className="divide-y divide-gray-200">
                      <tr>
                        <td className="py-2.5 pr-4 text-gray-700 font-medium">Sıklık (Çözgü):</td>
                        <td className="py-2.5">{fabric.density?.warp || '-'}</td>
                      </tr>
                      <tr>
                        <td className="py-2.5 pr-4 text-gray-700 font-medium">Sıklık (Atkı):</td>
                        <td className="py-2.5">{fabric.density?.weft || '-'}</td>
                      </tr>
                      <tr>
                        <td className="py-2.5 pr-4 text-gray-700 font-medium">Gramaj:</td>
                        <td className="py-2.5">
                          {fabric.weight > 0 ? (
                            <span className="flex items-center">
                              <span className="font-medium">{fabric.weight}</span>
                              <span className="text-gray-500 ml-1 text-xs">gr/m²</span>
                            </span>
                          ) : '-'}
                        </td>
                      </tr>
                      <tr>
                        <td className="py-2.5 pr-4 text-gray-700 font-medium">Kumaş Eni:</td>
                        <td className="py-2.5">
                          {fabric.width > 0 ? (
                            <span className="flex items-center">
                              <span className="font-medium">{fabric.width}</span>
                              <span className="text-gray-500 ml-1 text-xs">cm</span>
                            </span>
                          ) : '-'}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Fiyat Bilgileri - Her zaman göster */}
            <div className="mb-8">
              <h2 className="text-xl font-bold mb-4 text-gray-800 border-b border-gray-200 pb-2 flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                </svg>
                Fiyat Bilgileri
              </h2>
              <div className="bg-green-50/60 p-4 rounded-lg border border-green-100">
                <table className="w-full text-sm">
                  <tbody className="divide-y divide-green-100">
                    <tr>
                      <td className="py-2.5 pr-4 text-gray-700 font-medium">Fiyat:</td>
                      <td className="py-2.5">
                        {fabric.price > 0 ? (
                          <span className="flex items-center">
                            <span className="font-semibold text-green-800 text-lg">{fabric.price.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            {fabric.currency && (
                              <span className="text-green-600 ml-2 font-medium">{currencyValueToLabel(fabric.currency)}</span>
                            )}
                            {fabric.priceUnit && (
                              <span className="text-gray-500 ml-2 text-xs">/ {priceUnitValueToLabel(fabric.priceUnit)}</span>
                            )}
                          </span>
                        ) : (
                          <span className="text-gray-500">Belirtilmemiş</span>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td className="py-2.5 pr-4 text-gray-700 font-medium">Para Birimi:</td>
                      <td className="py-2.5">
                        {currencyValueToLabel(fabric.currency || 'TRY')}
                      </td>
                    </tr>
                    <tr>
                      <td className="py-2.5 pr-4 text-gray-700 font-medium">Fiyat Birimi:</td>
                      <td className="py-2.5">
                        {priceUnitValueToLabel(fabric.priceUnit || 'meter')}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Notlar */}
            {fabric.notes && (
              <div className="mb-6 bg-yellow-50 p-5 rounded-lg border border-yellow-100">
                <h2 className="text-lg font-semibold mb-2 text-yellow-800 flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 13V5a2 2 0 00-2-2H4a2 2 0 00-2 2v8a2 2 0 002 2h3l3 3 3-3h3a2 2 0 002-2zM5 7a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1zm1 3a1 1 0 100 2h3a1 1 0 100-2H6z" clipRule="evenodd" />
                  </svg>
                  Notlar
                </h2>
                <p className="text-yellow-700 whitespace-pre-line">{fabric.notes}</p>
              </div>
            )}

            {/* QR Kod ve Barkod için yer */}
            <div className="mt-10 border-t pt-6 flex justify-between items-center">
              <div>
                <span className="text-gray-500 text-xs flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                  </svg>
                  Kumaş ID: <span className="font-mono ml-1">{fabric._id}</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FabricDetail;
